import React, { useEffect, useRef } from 'react'
import { yupToFormErrors } from 'formik'
import yup from 'yup'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import { useHistory } from 'react-router'
import { useStores } from 'presentation/hooks/use-stores'
import { FormikProvider, useFormik } from 'formik'
import {
  ClinicalHistoryFormValues,
  OrderStepsPath
} from 'presentation/contexts/surgical-order-v2/types'
import FormClinicalHistory from 'presentation/doctor/components/Forms/SurgicalOrder/FormClinicalHistory'
import {
  WithLoading,
  WithLoadingProps
} from 'presentation/shared/components/HOCs/WithLoading'
import { observer } from 'mobx-react'
import { toJS } from 'mobx'
import { useServices } from 'presentation/hooks/use-services'
import { toast } from 'react-toastify'
import { LatexAllergyOptions } from 'common/enum/latex-allergy'
import validationSchema from './validationSchema'
import { draftValidationSchema } from './draftValidationSchema'

const ClinicalHistory = WithLoading(
  observer(({ setIsLoading }: WithLoadingProps) => {
    const history = useHistory()
    const store = useStores().surgicalOrderV2
    const surgicalOrderService = useServices().surgicalOrder
    const schemaRef = useRef<yup.AnyObjectSchema>(validationSchema)

    const formik = useFormik({
      initialValues: {
        ...toJS(store.stepClinicalHistoryFormValues),
        clinicalHistoryHospitalizationDailyQuantity:
          store.stepClinicalHistoryFormValues
            .clinicalHistoryHospitalizationMode === 'ambulatory'
            ? 0
            : store.stepClinicalHistoryFormValues
                .clinicalHistoryHospitalizationDailyQuantity || 1
      } as ClinicalHistoryFormValues,
      validate: (values) => {
        try {
          schemaRef.current.validateSync(values, { abortEarly: false })
          return {}
        } catch (error) {
          return yupToFormErrors(error)
        }
      },
      onSubmit: async (values: ClinicalHistoryFormValues) => {
        const isDraft = schemaRef.current === draftValidationSchema
        const updatedSurgicalOrder = await updateSurgicalOrder(values)

        if (updatedSurgicalOrder) {
          if (isDraft) {
            toast.success('As alterações foram salvas')
          } else {
            store.markStepCompleted(store.currentStepIndex)
            history.replace(OrderStepsPath.REPORTS_AND_DOCUMENTS)
          }
        }
      }
    })

    const updateSurgicalOrder = async (values: ClinicalHistoryFormValues) => {
      if (!store.surgicalOrder?.surgicalOrderId) return true

      const allergy =
        values.clinicalHistoryLatexAllergy === LatexAllergyOptions.NOT_INFORMED
          ? LatexAllergyOptions.NOT_INFORMED
          : values.clinicalHistoryLatexAllergy === LatexAllergyOptions.YES
          ? LatexAllergyOptions.YES
          : values.clinicalHistoryLatexAllergy === LatexAllergyOptions.NO
          ? LatexAllergyOptions.NO
          : ''

      const payload = {
        surgical_order_id: store.surgicalOrder.surgicalOrderId,
        patient: {
          patient_id: store.patient?.patientId,
          patient_name: store.patient?.name
        },
        clinical_history: {
          allergy: [
            allergy,
            values.clinicalHistoryOtherAllergy
              ? values.clinicalHistoryOtherAllergy?.trim()
              : ''
          ],
          blood_transfusion: values.clinicalHistoryBloodTransfusion,
          clinical_history: values.clinicalHistoryDescription?.trim() ?? '',
          psychological_aspects: values.clinicalHistoryAspects?.trim() ?? ''
        },
        clinical_record: {
          bullish_forecast_date: values.clinicalHistoryBullishForecastDate,
          cti: values.clinicalHistoryCti,
          extra_blood: values.clinicalHistoryBloodReserve,
          freeze: values.clinicalHistoryCongelation,
          hospitalization_type: values.clinicalHistoryHospitalizationType,
          medical_observation:
            values.clinicalHistoryMedicalObservations?.trim(),
          oncology_or_research: values.clinicalHistoryOncology,
          patient_hospitalized: values.clinicalHistoryHospitalized,
          hospitalization_mode: values.clinicalHistoryHospitalizationMode,
          requested_dailies: values.clinicalHistoryHospitalizationDailyQuantity,
          technique: values.clinicalHistoryTechnique
        },
        expected_date: values.clinicalHistoryExpectedDate
      } as any

      setIsLoading(true)
      try {
        await store.updateSurgicalOrder(surgicalOrderService, payload)
        return true
      } catch (error) {
        toast.error('Ocorreu um erro ao atualizar o pedido')
        return false
      } finally {
        setIsLoading(false)
      }
    }

    // Sincroniza validação do Formik com o Context para o botão do Layout
    useEffect(() => {
      store.setIsStepValid(formik.isValid && !formik.isSubmitting)
    }, [formik.isValid, formik.isSubmitting])

    // Escuta o clique do botão "Próximo"
    useEffect(() => {
      if (store.submitFormTriggered) {
        schemaRef.current = validationSchema
        formik.submitForm()
        store.resetSubmitTrigger()
      }
    }, [store.submitFormTriggered])

    // Escuta o clique do botão "Salvar alterações"
    useEffect(() => {
      if (store.saveOnlyTriggered) {
        schemaRef.current = draftValidationSchema
        formik.setErrors({})
        formik.setTouched({})
        formik.submitForm()
        store.resetSaveOnlyTrigger()
      }
    }, [store.saveOnlyTriggered])

    return (
      <>
        <HeadingNew
          as="h1"
          color="textPrimary"
          size="xlarge"
          style={{ lineHeight: 1 }}
        >
          História e ficha clínica
        </HeadingNew>
        <SupportTextNew
          color="textSecondary"
          fontFamily="secondary"
          size="medium"
          style={{ marginBottom: '24px' }}
        >
          Preencha história e ficha clínica do paciente para a cirurgia
        </SupportTextNew>

        <FormikProvider value={formik}>
          <FormClinicalHistory />
        </FormikProvider>
      </>
    )
  })
)

export default ClinicalHistory
